import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../api';
import { defaultProductPicture } from '../imageUtils';
import { cacheReceipts, getCachedReceipts, getPendingReceipts } from '../offlineDb';

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultStartDate() {
  const now = new Date();
  return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
}

export default function Receipts({ token, user }) {
  const [receipts, setReceipts] = useState([]);
  const [pendingReceipts, setPendingReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [product, setProduct] = useState(null);
  const [productResults, setProductResults] = useState([]);
  const [farmerQuery, setFarmerQuery] = useState('');
  const [farmer, setFarmer] = useState(null);
  const [farmerResults, setFarmerResults] = useState([]);
  const [buyerQuery, setBuyerQuery] = useState('');
  const [buyer, setBuyer] = useState(null);
  const [buyerResults, setBuyerResults] = useState([]);
  const [start, setStart] = useState(defaultStartDate());
  const [end, setEnd] = useState(toDateInputValue(new Date()));
  const canFilterFarmer = user?.role === 'buyer' || user?.role === 'admin';
  const canFilterBuyer = user?.role === 'farmer' || user?.role === 'admin';

  const pendingVisible = pendingReceipts.filter((item) => {
    const receipt = item.preview;
    if (product && receipt.product_id !== product.id) return false;
    if (farmer && receipt.farmer_id !== farmer.id) return false;
    if (buyer && receipt.buyer_id !== buyer.id) return false;
    if (start && receipt.timestamp < `${start}T00:00:00.000Z`) return false;
    if (end && receipt.timestamp > `${end}T23:59:59.999Z`) return false;
    return true;
  });

  const loadPending = async () => {
    setPendingReceipts(await getPendingReceipts());
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (!token || !productQuery || product?.name === productQuery) {
        setProductResults([]);
        return;
      }

      fetch(`${API}/products/search?q=${encodeURIComponent(productQuery)}`, { headers: { Authorization: token } })
        .then((res) => res.json())
        .then(setProductResults)
        .catch(() => setProductResults([]));
    }, 300);

    return () => clearTimeout(t);
  }, [productQuery, product, token]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!token || !farmerQuery || farmer?.name === farmerQuery) {
        setFarmerResults([]);
        return;
      }

      fetch(`${API}/farmers/search?q=${encodeURIComponent(farmerQuery)}`, { headers: { Authorization: token } })
        .then((res) => res.json())
        .then(setFarmerResults)
        .catch(() => setFarmerResults([]));
    }, 300);

    return () => clearTimeout(t);
  }, [farmerQuery, farmer, token]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!token || !buyerQuery || buyer?.name === buyerQuery) {
        setBuyerResults([]);
        return;
      }

      fetch(`${API}/buyers/search?q=${encodeURIComponent(buyerQuery)}`, { headers: { Authorization: token } })
        .then((res) => res.json())
        .then(setBuyerResults)
        .catch(() => setBuyerResults([]));
    }, 300);

    return () => clearTimeout(t);
  }, [buyerQuery, buyer, token]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    getCachedReceipts().then((cached) => {
      const filtered = cached.filter((receipt) => {
        if (product && receipt.product_id !== product.id) return false;
        if (farmer && receipt.farmer_id !== farmer.id) return false;
        if (buyer && receipt.buyer_id !== buyer.id) return false;
        if (start && receipt.timestamp < `${start}T00:00:00.000Z`) return false;
        if (end && receipt.timestamp > `${end}T23:59:59.999Z`) return false;
        return true;
      });
      setReceipts(filtered);
    });
    loadPending();

    const params = new URLSearchParams();
    if (product) params.set('product_id', product.id);
    if (farmer) params.set('farmer_id', farmer.id);
    if (buyer) params.set('buyer_id', buyer.id);
    if (start) params.set('start', start);
    if (end) params.set('end', end);

    setLoading(true);
    setError('');

    fetch(`${API}/receipts?${params.toString()}`, { headers: { Authorization: token } })
      .then(async (res) => {
        if (!res.ok) throw new Error(await res.text());
        return res.json();
      })
      .then((data) => {
        setReceipts(data);
        cacheReceipts(data);
      })
      .catch(() => setError('Could not load receipts. Make sure the backend is running.'))
      .finally(() => setLoading(false));
  }, [token, product, farmer, buyer, start, end]);

  useEffect(() => {
    const onUpdate = () => loadPending();
    window.addEventListener('offline-receipts-updated', onUpdate);
    return () => window.removeEventListener('offline-receipts-updated', onUpdate);
  }, []);

  const clearProduct = () => {
    setProduct(null);
    setProductQuery('');
    setProductResults([]);
  };

  const clearAll = () => {
    clearProduct();
    setFarmer(null);
    setFarmerQuery('');
    setFarmerResults([]);
    setBuyer(null);
    setBuyerQuery('');
    setBuyerResults([]);
  };

  const renderUserFilter = (label, query, setQuery, selected, setSelected, results, setResults) => (
    <div className="filterField">
      <label>{label}</label>
      <input
        placeholder={`Search ${label.toLowerCase()}`}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (selected && e.target.value !== selected.name) setSelected(null);
        }}
      />
      {results.length > 0 && (
        <div className="filterResults">
          {results.map((item) => (
            <button
              className="filterResult userFilterResult"
              key={item.id}
              onClick={() => {
                setSelected(item);
                setQuery(item.name);
                setResults([]);
              }}
            >
              <span>{item.name}</span>
              {(item.registration_number || item.username) && <small>{item.registration_number || item.username}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="wideContainer">
      <div className="pageHeader">
        <div>
          <h2>Receipts</h2>
          {user && <p>{user.role === 'farmer' ? 'Receipts issued to you.' : 'Receipts you can access.'}</p>}
        </div>
        {user?.role === 'buyer' && <Link to="/create"><button>Create Receipt</button></Link>}
      </div>

      <section className="filterPanel statsFilterPanel">
        <div className="filterField">
          <label>Product</label>
          <input
            placeholder="Search product"
            value={productQuery}
            onChange={(e) => {
              setProductQuery(e.target.value);
              if (product && e.target.value !== product.name) setProduct(null);
            }}
          />
          {productResults.length > 0 && (
            <div className="filterResults">
              {productResults.map((item) => (
                <button
                  className="filterResult"
                  key={item.id}
                  onClick={() => {
                    setProduct(item);
                    setProductQuery(item.name);
                    setProductResults([]);
                  }}
                >
                  <img className="productThumb" src={item.picture || defaultProductPicture} alt="" />
                  <span>{item.name}</span>
                  {item.reference_number && <small>{item.reference_number}</small>}
                </button>
              ))}
            </div>
          )}
        </div>

        {canFilterFarmer && renderUserFilter('Farmer', farmerQuery, setFarmerQuery, farmer, setFarmer, farmerResults, setFarmerResults)}
        {canFilterBuyer && renderUserFilter('Buyer', buyerQuery, setBuyerQuery, buyer, setBuyer, buyerResults, setBuyerResults)}

        <div className="filterField">
          <label>Start date</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>

        <div className="filterField">
          <label>End date</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>

        <button className="secondaryButton filterButton" onClick={clearAll}>Clear Filters</button>
      </section>

      {loading && <p>Loading...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && receipts.length === 0 && pendingVisible.length === 0 && <p>No receipts match these filters.</p>}

      <div className="receiptGrid">
        {pendingVisible.map((pending) => {
          const receipt = pending.preview;
          return (
            <article className="receiptCard pendingReceipt" key={pending.id}>
              <div className="receiptTop">
                <div>
                  <h3>{receipt.product_name}</h3>
                  {receipt.product_reference && <p>{receipt.product_reference}</p>}
                  <p>{receipt.quantity} {receipt.unit_name}</p>
                  <p className="pendingBadge">{pending.status === 'sync_conflict' ? 'Sync conflict - needs review' : 'Pending sync - not official'}</p>
                </div>
                <div className="receiptImages">
                  <img className="productReceiptImage" src={receipt.product_picture || defaultProductPicture} alt="" />
                </div>
              </div>

              <dl className="receiptMeta">
                <dt>Farmer</dt>
                <dd>{receipt.farmer_name}</dd>
                <dt>Date</dt>
                <dd>{new Date(receipt.timestamp).toLocaleString()}</dd>
                <dt>Local ID</dt>
                <dd className="mono">{pending.id}</dd>
              </dl>
            </article>
          );
        })}

        {receipts.map((receipt) => (
          <article className="receiptCard" key={receipt.id}>
            <div className="receiptTop">
              <div>
                <h3>{receipt.product_name}</h3>
                {receipt.product_reference && <p>{receipt.product_reference}</p>}
                <p>{receipt.quantity} {receipt.unit_name}</p>
              </div>
              <div className="receiptImages">
                <img className="productReceiptImage" src={receipt.product_picture || defaultProductPicture} alt="" />
                {receipt.qr && <img className="qrImage" src={receipt.qr} alt={`QR code for receipt ${receipt.id}`} />}
              </div>
            </div>

            <dl className="receiptMeta">
              <dt>Farmer</dt>
              <dd>{receipt.farmer_name}</dd>
              <dt>Buyer</dt>
              <dd>{receipt.buyer}</dd>
              <dt>Date</dt>
              <dd>{new Date(receipt.timestamp).toLocaleString()}</dd>
              <dt>ID</dt>
              <dd className="mono">{receipt.id}</dd>
              <dt>Hash</dt>
              <dd className="hashText">{receipt.hash}</dd>
            </dl>

            <div className="receiptActions">
              <Link to={`/verify/${receipt.id}`}>Verify</Link>
              <a href={`${API}/receipt/${receipt.id}/pdf`} target="_blank" rel="noreferrer">PDF</a>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
